import React, { useState, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import { matchPath } from 'react-router-dom';
import EMAILS from 'constants/emails';
import { routes } from 'constants/config';
import request from 'utils/request_ajax';
import useFeatureFlags from 'hooks/useFeatureFlags';

import { BetaFeedback } from 'components/BetaProgram';
import { Text, TEXT_VARIANTS } from 'ui/Typography';
import WordsPerMinuteModule from './WordsPerMinuteModule';
import FillerWordUsageModule from './FillerWordUsageModule';
import KeywordMatchingSection from './KeywordMatchingSection';
import ConfidenceModule from './ConfidenceModule';
import TranscriptSection from './TranscriptSection';

const AutomatedAnalysisContainer = ({ data }) => {
  const [assessmentLoading, setAssessmentLoading] = useState(true);
  const [assessmentError, setAssessmentError] = useState();
  const [assessmentData, setAssessmentData] = useState({});
  const [metricsCount, setMetricsCount] = useState(0);

  const inDevelopment = useFeatureFlags(['automated_analysis_in_development']);

  const isKeywordMatching =
    !assessmentLoading && data.questionAutomatedAnalysisKeywordMatchingEnabled;

  const isWordsPerMinute =
    !assessmentLoading && data.questionAutomatedAnalysisWordsPerMinuteEnabled;

  const isFillerWords =
    !assessmentLoading && data.questionAutomatedAnalysisFillerWordsEnabled;

  const isLibraryPath = pathname => !!matchPath(pathname, { path: '/library' });
  const isPathPath = pathname => !!matchPath(pathname, { path: '/path' });
  const pathIdMatch = pathname => {
    const match = matchPath(pathname, {
      path: '/path/:pathId/lesson/:lessonId',
    })?.params?.pathId;
    return ([match] || [undefined])[0];
  };
  const lessonIdMatch = pathname => {
    const match = matchPath(pathname, {
      path: '/path/:pathId/lesson/:lessonId',
    })?.params?.lessonId;
    return ([match] || [undefined])[0];
  };

  const fetchAssessmentData = answerId => {
    let assignmentType;
    let pathId;
    if (isLibraryPath(window.location.pathname)) assignmentType = 'library';
    else if (isPathPath(window.location.pathname))
      pathId = pathIdMatch(window.location.pathname);
    else assignmentType = 'assignment';
    const lessonId = lessonIdMatch(window.location.pathname);

    request
      .get(
        routes.ASSESSMENTS.ANSWER(answerId, assignmentType, lessonId, pathId),
      )
      .then(responseData => {
        setAssessmentData(responseData);
        setAssessmentLoading(false);
        setAssessmentError();
      })
      .catch(e => {
        setAssessmentError(e);
        setAssessmentLoading(false);
      });
  };

  useEffect(
    () => {
      if (data.questionAutomatedAnalysis) {
        fetchAssessmentData(data.id);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data],
  );
  useEffect(() => {
    let tempCount = 0;
    if (isWordsPerMinute) {
      tempCount += 1;
    }
    if (isFillerWords) {
      tempCount += 1;
    }
    if (inDevelopment) {
      tempCount += 1;
    }
    setMetricsCount(tempCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWordsPerMinute, isFillerWords, inDevelopment]);

  return (
    <div className="AutomatedAnalysisContainer">
      {assessmentLoading && (
        <>
          <Skeleton count={1} height={120} className="mt-32" />
          <div className="d-f jc-sb mb-12 mt-12 w-100p">
            <div className="w-48p mr-6">
              {' '}
              <Skeleton count={1} height={150} />
            </div>
            <div className="w-48p">
              <Skeleton count={1} height={150} />
            </div>
          </div>
          <Skeleton count={3} height={20} className="mt-12" />
        </>
      )}

      {!assessmentLoading && (
        <div className={`metrics-modules-${metricsCount}`}>
          <div className="mt-16">
            <Text variant={TEXT_VARIANTS.SUBHEADER_BOLD}>
              {t('automated_analysis.automated_analysis_overall.title')}
            </Text>
          </div>

          {isKeywordMatching && (
            <KeywordMatchingSection
              assessmentData={assessmentData}
              assessmentError={assessmentError}
            />
          )}
          {metricsCount > 0 && (
            <div
              className="metrics-modules
              d-f mt-24 fw-w jc-sb"
            >
              {isWordsPerMinute && (
                <WordsPerMinuteModule
                  assessmentData={assessmentData}
                  assessmentError={assessmentError}
                  componentIndex={1}
                />
              )}

              {isFillerWords && (
                <FillerWordUsageModule
                  assessmentData={assessmentData}
                  assessmentError={assessmentError}
                  componentIndex={2}
                />
              )}

              {inDevelopment && (
                <ConfidenceModule
                  assessmentData={assessmentData}
                  assessmentError={assessmentError}
                  componentIndex={3}
                />
              )}
            </div>
          )}

          <TranscriptSection
            assessmentData={assessmentData}
            assessmentError={assessmentError}
          />
        </div>
      )}

      {!assessmentLoading && (
        <BetaFeedback
          className="mt-12"
          betaFeatureName="Automated Analysis"
          recipient={EMAILS.PRACTICE_FEEDBACK}
          feedbackWithFeatureName
          inline
        />
      )}
    </div>
  );
};

export default AutomatedAnalysisContainer;
